import { useEffect, useState } from 'react';

import { Button } from '@/components/ui.js';

export const OAUTH_RESULT_MESSAGE = 'shopx-distribution:oauth-result';

export interface OAuthResultMessage {
  type: typeof OAUTH_RESULT_MESSAGE;
  connected: string | null;
  error: string | null;
}

/**
 * Landing page for the OAuth popup.
 *
 * Ad platforms refuse to render consent screens inside a third-party iframe, so
 * authorization happens in a separate tab. That tab has no `tenantUrl` and can
 * never complete the handshake, which is why this page sits outside the gate:
 * it only relays the outcome back to the opener and gets out of the way.
 */
export function OAuthCompletePage() {
  const [closed, setClosed] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const connected = params.get('connected');

  useEffect(() => {
    const message: OAuthResultMessage = {
      type: OAUTH_RESULT_MESSAGE,
      connected,
      error,
    };
    // Same origin on both sides: the opener is this app running in the iframe.
    window.opener?.postMessage(message, window.location.origin);

    if (error) return;
    const timer = window.setTimeout(() => {
      window.close();
      setClosed(true);
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [connected, error]);

  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md rounded-brand border border-slate-200 bg-white p-6 text-center">
        <h1 className="text-base font-semibold">
          {error ? 'Could not connect the account' : 'Account connected'}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {error ?? 'You can close this tab and continue in Kadanza.'}
        </p>
        {closed ? null : (
          <Button className="mt-4" variant="secondary" onClick={() => window.close()}>
            Close tab
          </Button>
        )}
      </div>
    </div>
  );
}
